import { ENV } from '@qse/constants'

/**
 * Normalize URL path by removing trailing slashes
 * This prevents issues with route matching when browsers add trailing slashes
 */
export const normalizePath = (path: string): string => {
  // Remove trailing slash, but keep root path as '/'
  return path === '/' ? path : path.replace(/\/$/, '')
}

/**
 * Check if current path matches any of the given routes (ignoring trailing slashes)
 */
export const isPathMatch = (currentPath: string, targetRoutes: string[]): boolean => {
  const normalizedCurrentPath = normalizePath(currentPath)

  return targetRoutes.some(route => normalizePath(route) === normalizedCurrentPath)
}

/**
 * Get the admin app URL based on environment variables with fallbacks
 */
export const getAdminUrl = (path: string = ''): string => {
  // If environment variable is set, use it
  if (ENV.ADMIN_APP_URL) {
    return `${ENV.ADMIN_APP_URL}${path}`
  }

  // Fallback to hostname-based detection
  const currentHostname = window.location.hostname

  // Development environment
  if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
    return `http://localhost:5174${path}` // Admin app port in development
  } else if (currentHostname.includes('dev')) {
    return `https://dev.admin.cloud.qse-corp.com${path}`
  } else if (currentHostname.includes('cloud.qse-corp.com')) {
    // Production environment
    return `https://admin.cloud.qse-corp.com${path}`
  } else {
    // Fallback for other environments
    return `https://admin.${currentHostname.replace(/^[^.]+\./, '')}${path}`
  }
}

/**
 * Get the dashboard app URL based on environment variables with fallbacks
 */
export const getDashboardUrl = (path: string = ''): string => {
  const normalizeBase = (url: string) => url.replace(/\/+$/, '')

  const resolveUrl = (base: string, targetPath: string) => {
    const trimmedBase = normalizeBase(base)

    if (!targetPath) {
      return trimmedBase
    }

    try {
      const normalizedTarget = targetPath.startsWith('/') ? targetPath : `/${targetPath}`

      return new URL(normalizedTarget, `${trimmedBase}/`).toString()
    } catch {
      return `${trimmedBase}${targetPath}`
    }
  }

  const normalizedPath = typeof path === 'string' ? path.trim() : ''

  // Fallback to hostname-based detection
  const currentHostname = window.location.hostname

  const fallbackUrl = () => {
    if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
      return 'http://localhost:5173'
    }

    if (currentHostname.includes('dev')) {
      return 'https://dev.cloud.qse-corp.com'
    }

    if (currentHostname.includes('cloud.qse-corp.com')) {
      return 'https://cloud.qse-corp.com'
    }

    return `https://${currentHostname.replace(/^[^.]+\./, '')}`
  }

  // If environment variable is set, use it
  if (ENV.DASHBOARD_APP_URL) {
    return resolveUrl(ENV.DASHBOARD_APP_URL, normalizedPath)
  }

  // Development environment
  return resolveUrl(fallbackUrl(), normalizedPath)
}

export const getQpaUrl = (path: string = ''): string => {
  const normalizeBase = (url: string) => url.replace(/\/+$/, '')
  const resolveUrl = (base: string, targetPath: string) => {
    const trimmedBase = normalizeBase(base)

    if (!targetPath) {
      return trimmedBase
    }

    try {
      const normalizedTarget = targetPath.startsWith('/') ? targetPath : `/${targetPath}`

      return new URL(normalizedTarget, `${trimmedBase}/`).toString()
    } catch {
      return `${trimmedBase}${targetPath}`
    }
  }

  const normalizedPath = typeof path === 'string' ? path.trim() : ''

  // Fallback to hostname-based detection
  const currentHostname = window.location.hostname

  function fallbackUrl() {
    if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
      return 'http://localhost:5176'
    }

    if (currentHostname.includes('dev')) {
      return 'https://dev.qpa.qse-corp.com'
    }

    if (currentHostname === 'cloud.qse-corp.com') {
      return 'https://qpa.qse-corp.com'
    }

    return `https://${currentHostname.replace(/^[^.]+\./, '')}`
  }

  if (ENV.QPA_URL) {
    return resolveUrl(ENV.QPA_URL, normalizedPath)
  }

  return resolveUrl(fallbackUrl(), normalizedPath)
}

/**
 * Get the admin app register URL
 */
export const getAdminRegisterUrl = (): string => {
  return getAdminUrl('/auth/register')
}

/**
 * Get the dashboard app login URL
 */
export const getDashboardLoginUrl = (): string => {
  return getDashboardUrl('/auth/login')
}
